import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { addHours, format, parse } from 'date-fns';
import SelectFieldShuttle from 'components/Global/SelectFieldShuttle';
import {
  InputField,
  SelectFieldAdditionalItem,
  SelectFieldAirportPackage,
  SelectFieldPassengerAndLuggage,
} from 'components/Global';
import SelectFieldDate from 'components/Global/SelectFieldDate';
import SelectFieldTime from 'components/Global/SelectFieldTime';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import { ReactComponent as SwitchIcon } from 'icons/switch-icon.svg';
import { useAppContext } from 'components/Context/AppContext';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSummaryAirportTransferOrder } from 'features/orders/actions';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import { checkEmptyObject, setCurrency } from 'utils/functionality';
import CheckboxField from 'components/Global/CheckboxField';

const DefaultForm = forwardRef(
  (
    {
      nextPage,
      deliveryLocation,
      isSwitch,
      switchHandler,
      setDeliveryLocation,
      setOrderData,
      orderData,
      rentalLocation,
      returnLocation,
      setReturnLocation,
      startRentDate,
      setStartRentDate,
      setEndRentDate,
      selectedCar,
      setSelectedCar,
      numberInputOnWheelPreventChange,
      isOrderConfirmation,
      selectedAdditionalItem,
      setSelectedAdditionalItem,
      isMeetAndGreetBoardRequire,
      setIsMeetAndGreetBoardRequire,
      selectedLuggage,
      selectedPassengers,
      setSelectedPassengers,
      setSelectedLuggage,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const { showToast } = useAppContext();

    const { data: rentalServicesData } = useSelector((state) => state.services);

    // GLOBAL STATE
    const { dataZone, dataAirport } = useSelector((state) => state.shuttleAirport);
    const { data: vehicleAirportPackageData, selectedPackageVechile } = useSelector(
      (state) => state.vehicleAirportPackage,
    );
    const user = useSelector((state) => state.user.profile);
    const { data: additionalData } = useSelector((state) => state.addon);
    const { data: summaryOrderData } = useSelector((state) => state.summaryOrder);

    // ADDITIONAL INPUT SEARCH VALUE
    const [additionalSearchInput, setAdditionalSearchInput] = useState('');

    const isImprovUpdateDataEnabled = useFliptBoolean(
      'FLAG_FE_IMPROVEMENT_UPDATE_DATA_ORDER',
      false,
      user.id || 'anonymous',
    );

    const getFilterParamsAddons = () => {
      let endBookingTime = orderData?.order_detail?.start_booking_time;
      let endBookingDate = orderData?.order_detail?.start_booking_date;

      if (endBookingTime) {
        const parsedDate = parse(`${endBookingDate} ${endBookingTime}`, 'yyyy-MM-dd HH:mm', new Date());

        const plusOneHours = addHours(parsedDate, 3);
        endBookingTime = format(plusOneHours, 'HH:mm');
      }

      return {
        locationId: rentalLocation?.id,
        locTimeId: rentalLocation?.time_zone_identifier,
        startDate: orderData.order_detail?.start_booking_date,
        endDate: endBookingDate,
        startTime: orderData.order_detail?.start_booking_time + ':00',
        endTime: endBookingTime + ':00',
      };
    };

    // ENTER SELECTCAR IF SELECTCAR HAS BEEN ENTERED BEFORE
    useEffect(() => {
      if (!selectedCar) return;

      setOrderData({
        ...orderData,
        order_detail: { ...orderData.order_detail, vehicle_id: selectedCar?.vehicle_id },
      });
    }, [selectedCar]);

    const passangerAndLuggageHandler = (passengers, luggage) => {
      setSelectedPassengers(passengers);
      setSelectedLuggage(luggage);
      setOrderData({
        ...orderData,
        order_detail: {
          ...orderData.order_detail,
          adult_passenger: passengers.adults,
          child_passenger: passengers.child,
          // regular_suitcase: luggage.luggage,
          // large_suitcase: luggage.suitcaseXl,
          baggage: luggage.luggage,
        },
      });
    };

    const submitHandler = async () => {
      // validation
      const validDeliveryLocation = orderData.order_detail.rental_delivery_location !== '';
      const validReturnLocation = orderData.order_detail.rental_return_location !== '';
      const validDetailDeliveryLocation = orderData.order_detail.rental_delivery_location_detail.trim() !== '';
      const validDetailReturnLocation = orderData.order_detail.rental_return_location_detail.trim() !== '';
      const validStartBookingDate = orderData.order_detail.start_booking_date !== '';
      const validStartBookingTime = orderData.order_detail.start_booking_time !== '';
      const validFlightNumber = orderData.order_detail.flight_number.trim() !== '';
      const validVehicle = selectedCar && Object.keys(selectedCar).length !== 0;
      const validPackage = orderData.airport_transfer_package_id;
      const validBaggage = isNaN(orderData.order_detail.baggage) || orderData.order_detail.baggage === '';
      const validMeetAndGreetBoard =
        orderData.customer_name_meet_and_greet && orderData.customer_name_meet_and_greet !== '';

      // check validation
      if (!validDeliveryLocation) {
        return showToast({ type: 'error', message: 'Pilih Lokasi Penjemputan' });
      } else if (!validReturnLocation) {
        return showToast({ type: 'error', message: 'Pilih Lokasi Pengantaran' });
      } else if (!validDetailDeliveryLocation) {
        return showToast({ type: 'error', message: 'Masukan Detail Lokasi Penjemputan' });
      } else if (!validDetailReturnLocation) {
        return showToast({ type: 'error', message: 'Masukan Detail Lokasi Pengantaran' });
      } else if (!validStartBookingDate) {
        return showToast({ type: 'error', message: 'Pilih Tanggal' });
      } else if (!validStartBookingTime) {
        return showToast({ type: 'error', message: 'Pilih Jam Jemput' });
      } else if (!validFlightNumber && isSwitch) {
        return showToast({ type: 'error', message: 'Masukan Nomor Penerbangan' });
      } else if (!validPackage) {
        return showToast({ type: 'error', message: 'Pilih Package Mobil' });
      } else if (!validVehicle) {
        return showToast({ type: 'error', message: 'Pilih Mobil' });
      } else if (validBaggage) {
        return showToast({ type: 'error', message: 'Masukan Banyak Koper yang dibawa' });
      } else if (isMeetAndGreetBoardRequire && !validMeetAndGreetBoard && isSwitch) {
        return showToast({ type: 'error', message: 'Masukan Meet And Greet Board' });
      }

      const subServiceAirportTransfer = rentalServicesData
        ?.find((item) => item.name === 'Sewa Mobil')
        ?.sub_services?.find((item) => item.name === 'Airport Transfer');

      let mappingAdditionalItem = [];

      if (selectedAdditionalItem) {
        for (let addItionalChild of selectedAdditionalItem) {
          const additionalObj = {
            id: addItionalChild.additional.id,
            varieties: [
              {
                id: addItionalChild.additional.varieties[0].id,
                quantity: addItionalChild.total,
              },
            ],
          };

          mappingAdditionalItem.push(additionalObj);
        }
      }

      // GET ORDER SUMMARY
      const payload = {
        order_type_id: orderData.order_type_id,
        start_booking_date: orderData.order_detail.start_booking_date,
        start_booking_time: orderData.order_detail.start_booking_time,
        airport_transfer_package_id: orderData.airport_transfer_package_id,
        location_id: rentalLocation?.id,
        sub_services_id: subServiceAirportTransfer?.id,
        end_booking_date: orderData.order_detail.end_booking_date,
        end_booking_time: orderData.order_detail.end_booking_time,
        rental_delivery_id: deliveryLocation?.id,
        rental_return_id: returnLocation?.id,
        addons: mappingAdditionalItem.length === 0 ? undefined : mappingAdditionalItem,
        use_meet_greet_board: isMeetAndGreetBoardRequire,
      };

      try {
        const response = await dispatch(fetchSummaryAirportTransferOrder(payload));
        if (response.meta?.requestStatus === 'fulfilled') {
          nextPage();
          return;
        }
        showToast({ type: 'error', message: 'Terjadi Kesalahan' });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        showToast({ type: 'error', message: 'Terjadi Kesalahan' });
      }
    };

    useEffect(() => {
      if (
        orderData.order_detail.start_booking_date === '' ||
        orderData.order_detail.start_booking_time === '' ||
        !orderData.airport_transfer_package_id ||
        checkEmptyObject(rentalLocation) ||
        checkEmptyObject(deliveryLocation) ||
        checkEmptyObject(returnLocation) ||
        isOrderConfirmation
      )
        return;

      let mappingAdditionalItem = [];

      if (selectedAdditionalItem) {
        for (let addItionalChild of selectedAdditionalItem) {
          const additionalObj = {
            id: addItionalChild.additional.id,
            varieties: [
              {
                id: addItionalChild.additional.varieties[0].id,
                quantity: addItionalChild.total,
              },
            ],
          };

          mappingAdditionalItem.push(additionalObj);
        }
      }

      const subServiceAirportTransfer = rentalServicesData
        ?.find((item) => item.name === 'Sewa Mobil')
        ?.sub_services?.find((item) => item.name === 'Airport Transfer');

      dispatch(
        fetchSummaryAirportTransferOrder({
          order_type_id: orderData.order_type_id,
          start_booking_date: orderData.order_detail.start_booking_date,
          start_booking_time: orderData.order_detail.start_booking_time,
          airport_transfer_package_id: orderData.airport_transfer_package_id,
          location_id: rentalLocation?.id,
          sub_services_id: subServiceAirportTransfer?.id,
          end_booking_date: orderData.order_detail.end_booking_date,
          end_booking_time: orderData.order_detail.end_booking_time,
          rental_delivery_id: deliveryLocation?.id,
          rental_return_id: returnLocation?.id,
          addons: mappingAdditionalItem.length === 0 ? undefined : mappingAdditionalItem,
          use_meet_greet_board: isMeetAndGreetBoardRequire,
        }),
      );
    }, [
      rentalServicesData,
      orderData.order_detail.start_booking_date,
      orderData.order_detail.start_booking_time,
      orderData.airport_transfer_package_id,
      rentalLocation,
      deliveryLocation,
      returnLocation,
      selectedAdditionalItem,
    ]);

    useImperativeHandle(ref, () => ({
      actionInGrandGrandChild() {
        submitHandler();
      },
    }));

    return (
      <>
        <div className="airport-transfer-form__form__shuttle">
          {/* DELIVERY LOCATION */}
          <SelectFieldShuttle
            label="Lokasi Penjemputan"
            htmlFor="lokasi-penjemputan"
            value={deliveryLocation.id}
            data={isSwitch ? dataAirport?.shuttle : dataZone?.shuttle}
            onChange={(item) => {
              setDeliveryLocation(item);
              setOrderData({
                ...orderData,
                order_detail: { ...orderData.order_detail, rental_delivery_location: item.name },
              });
            }}
            placeholder="Masukan Lokasi Penjemputan"
            disable={!rentalLocation.id ? true : isOrderConfirmation ? true : false}
            // intersectionAction={isSwitch ? null : intersectionHandler}
            searchFeature
          />

          {/* RETURN LOCATION */}
          <SelectFieldShuttle
            label="Lokasi Pengantaran"
            htmlFor="lokasi-pengantaran"
            value={returnLocation.id}
            data={isSwitch ? dataZone?.shuttle : dataAirport?.shuttle}
            onChange={(item) => {
              setReturnLocation(item);
              setOrderData({
                ...orderData,
                order_detail: { ...orderData.order_detail, rental_return_location: item.name },
              });
            }}
            placeholder="Masukan Lokasi Pengantaran"
            disable={!rentalLocation.id ? true : isOrderConfirmation ? true : false}
            // intersectionAction={isSwitch ? intersectionHandler : null}
            searchFeature
          />

          {/* ICON SWITCH */}
          {!isOrderConfirmation && <SwitchIcon className="switch" onClick={switchHandler} />}
        </div>

        {/* DETAIL DELIVERY LOCATION */}
        <InputField
          label="Detail Lokasi"
          htmlFor="detail-lokasi-penjemputan"
          placeholder="Tulis detail lokasi"
          value={orderData.order_detail.rental_delivery_location_detail}
          onChange={(e) =>
            setOrderData({
              ...orderData,
              order_detail: { ...orderData.order_detail, rental_delivery_location_detail: e.target.value },
            })
          }
          disable={isOrderConfirmation}
        />

        {/* DETAIL RETURN LOCATION */}
        <InputField
          label="Detail Lokasi"
          htmlFor="detail-lokasi-pengantaran"
          placeholder="Tulis detail lokasi"
          value={orderData.order_detail.rental_return_location_detail}
          onChange={(e) =>
            setOrderData({
              ...orderData,
              order_detail: { ...orderData.order_detail, rental_return_location_detail: e.target.value },
            })
          }
          disable={isOrderConfirmation}
        />

        <div className="airport-transfer-form__form__baggage-passenger">
          <div className="start-date">
            {/* START DATE */}
            <SelectFieldDate
              label="Tanggal"
              htmlFor="tanggal-mulai"
              name="tanggal-mulai"
              handleDaySelect={(date) => {
                if (date) {
                  setStartRentDate(date);
                  setEndRentDate('');
                  setOrderData({
                    ...orderData,
                    order_detail: {
                      ...orderData.order_detail,
                      start_booking_date: format(date, 'yyyy-MM-dd').toString(),
                    },
                  });
                }
                setSelectedAdditionalItem(undefined);
              }}
              selectedDay={startRentDate}
              value={startRentDate !== '' ? format(startRentDate, 'dd-MM-yyyy') : startRentDate}
              placeholder="Pilih Tanggal"
              disable={isOrderConfirmation}
            />
          </div>

          {/* START TIME */}
          <SelectFieldTime
            label="Jam Jemput"
            htmlFor="jam-mulai"
            placeholder="00:00"
            value={orderData.order_detail.start_booking_time}
            onChange={(hour, minute) => {
              let endBookingTime = `${hour}:${minute ? minute : '00'}`;
              let endBookingDate = orderData.order_detail.start_booking_date;

              const parsedDate = parse(`${endBookingDate} ${endBookingTime}`, 'yyyy-MM-dd HH:mm', new Date());
              const plusThreeHours = addHours(parsedDate, 3);
              endBookingTime = format(plusThreeHours, 'HH:mm');
              endBookingDate = format(plusThreeHours, 'yyyy-MM-dd');

              setOrderData({
                ...orderData,
                order_detail: {
                  ...orderData.order_detail,
                  start_booking_time: `${hour}:${minute ? minute : '00'}`,
                  end_booking_time: endBookingTime,
                  end_booking_date: endBookingDate,
                },
              });
              setSelectedAdditionalItem(undefined);
            }}
            disable={isOrderConfirmation ? true : startRentDate === '' ? true : false}
            showAllHours={true}
          />
        </div>

        {/* PACKAGE VEHICLE */}
        <SelectFieldDropdown
          label="Pilih Package Mobil"
          htmlFor="pilih-package-mobil"
          placeholder="Pilih Package Mobil"
          data={vehicleAirportPackageData?.packages}
          value={orderData?.airport_transfer_package_id}
          onChange={(e) => {
            setOrderData({
              ...orderData,
              airport_transfer_package_id: +e.target.id,
            });
            setSelectedCar(undefined);
          }}
          disable={
            isOrderConfirmation
              ? true
              : orderData.order_detail.start_booking_date === '' ||
                orderData.order_detail.start_booking_time === '' ||
                orderData.order_detail.rental_delivery_location === '' ||
                orderData.order_detail.rental_return_location === ''
          }
        />

        {/* FLIGTH NUMBER */}
        <InputField
          label="No. Penerbangan"
          htmlFor="no-penerbangan"
          placeholder="Tulis No. Penerbangan"
          value={orderData.order_detail.flight_number}
          onChange={(e) =>
            setOrderData({
              ...orderData,
              order_detail: { ...orderData.order_detail, flight_number: e.target.value },
            })
          }
          disable={isOrderConfirmation || !isSwitch}
        />

        {/* VEHICLE */}
        <SelectFieldAirportPackage
          label="Pilih Mobil"
          placeholder="Cari Mobil ..."
          id="airport-package"
          data={selectedPackageVechile?.vehicles}
          selectedCar={selectedCar}
          onSelectCar={setSelectedCar}
          disable={
            isOrderConfirmation
              ? true
              : orderData.order_detail.start_booking_date === '' ||
                orderData.order_detail.start_booking_time === '' ||
                !orderData.airport_transfer_package_id
          }
        />

        <div className="airport-transfer-form__form__baggage-passenger">
          <InputField label="Kapasitas Koper" value={selectedCar?.max_suitecase || 0} disable />
          {!isImprovUpdateDataEnabled ? (
            <InputField
              type="number"
              label="Koper yang dibawa"
              htmlFor="baggage"
              placeholder="Tulis Jumlah Koper"
              value={orderData.order_detail.baggage}
              onChange={(e) =>
                setOrderData({
                  ...orderData,
                  order_detail: {
                    ...orderData.order_detail,
                    baggage: isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value),
                  },
                })
              }
              disable={isOrderConfirmation}
              onWheel={numberInputOnWheelPreventChange}
            />
          ) : (
            <SelectFieldPassengerAndLuggage
              label="Passengers and Suitcase"
              htmlFor="passengers-and-suitcase"
              selectedLuggage={selectedLuggage}
              selectedPassengers={selectedPassengers}
              onSelect={passangerAndLuggageHandler}
              disable={isOrderConfirmation || !selectedCar}
              maxSuitCase={selectedCar ? selectedCar.max_suitecase : 0}
              maxPassenger={selectedCar ? selectedCar.max_passenger : 0}
            />
          )}
        </div>

        {isSwitch && summaryOrderData?.meet_greet_board?.is_active && (
          <div className="airport-transfer-form__form__meet-greet-board">
            <CheckboxField
              label="Meet&Greet Board (Optional)"
              name="meet-greet-board"
              value="meet-greet-board"
              className="meet-greet-board-checkbox"
              checked={isMeetAndGreetBoardRequire}
              onChange={(e) => {
                if (!e.target.checked) {
                  setOrderData({
                    ...orderData,
                    customer_name_meet_and_greet: '',
                  });
                }
                setIsMeetAndGreetBoardRequire(e.target.checked);
              }}
              disable={isOrderConfirmation}
            />
            <InputField
              label="Meet and Greet Board"
              htmlFor="meet-and-greet-board"
              placeholder="Welcome Big Ben!”, “Ben”, etc"
              value={orderData?.customer_name_meet_and_greet || ''}
              onChange={(e) =>
                setOrderData({
                  ...orderData,
                  customer_name_meet_and_greet: e.target.value,
                })
              }
              disable={isOrderConfirmation || !isMeetAndGreetBoardRequire}
            />
          </div>
        )}

        {/* ADDITIONAL ITEM */}
        <div className="airport-transfer-form__form__additional-item">
          <h2>Additional</h2>
          <div className="wrapper">
            <SelectFieldAdditionalItem
              label="Pilih Tambahan"
              id="tambahan"
              className="additional"
              placeholder="Cari Perlengkapan Khusus.."
              selectedAdditionalItem={selectedAdditionalItem}
              setSelectedAdditionalItem={setSelectedAdditionalItem}
              value={additionalSearchInput}
              onChange={(e) => setAdditionalSearchInput(e.target.value)}
              data={additionalData?.data || []}
              disable={isOrderConfirmation || !additionalData || checkEmptyObject(additionalData)}
              filterParams={getFilterParamsAddons()}
            />

            <InputField
              label="Total Biaya Permintaan Khusus"
              className="input-total-biaya-permintaan"
              placeholder="IDR 0"
              value={
                summaryOrderData?.addon_total_price && selectedAdditionalItem
                  ? setCurrency(summaryOrderData.addon_total_price)
                  : ''
              }
              disable
            />
          </div>
        </div>
      </>
    );
  },
);

DefaultForm.displayName = 'DefaultForm';
export default DefaultForm;
